import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Heart, ShoppingBag, Star, X, MessageSquare, Cpu, 
  Zap, Users, Send, User, BadgeCheck, Radio, 
  BatteryCharging, CheckCircle2, ArrowRight, ShieldCheck, Truck, Award,
  ChevronLeft, ChevronRight, Share2, Info
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import SEO from '../components/SEO';

// --- PREMIUM HUD ALERT SYSTEM ---
const CustomAlert = ({ message, onClose, type = "success" }) => {
  const isError = type === "error";
  return (
    <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-md animate-in slide-in-from-top-10 duration-500">
      <div className={`backdrop-blur-2xl border px-6 py-4 rounded-[2rem] shadow-2xl flex items-center justify-between ${isError ? "bg-red-500/90 border-red-400" : "bg-emerald-500/90 border-emerald-400"} text-white`}>
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-full"><Zap size={14} fill="white"/></div>
          <span className="font-black uppercase text-[10px] tracking-widest italic">{message}</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-black/20 rounded-full transition-transform active:scale-90"><X size={16}/></button>
      </div>
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart, toggleWishlist, wishlistItems } = useCart();
  const scrollRef = useRef(null);

  // --- COMPREHENSIVE STATES ---
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [alertMsg, setAlertMsg] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [showSizeAI, setShowSizeAI] = useState(false);
  const [aiData, setAIData] = useState({ height: "", weight: "" });
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [newReview, setNewReview] = useState({ rating: 5, comment: "", username: "" });
  const [sizeStockMap, setSizeStockMap] = useState({});
  const [currentStockDisplay, setCurrentStockDisplay] = useState(0);
  const [viewers] = useState(Math.floor(Math.random() * 25) + 8);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([
    { username: "System", text: "Welcome to the Collaborative Channel. Ask about fits!" }
  ]);

  // --- CORE LOGIC & DATA FETCH ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`https://wethrift-backend.onrender.com/api/product/find/${id}`);
        setProduct(res.data);
        setReviews(res.data.reviews || []);
        
        if (res.data.sizeStock?.length > 0) {
          const stock = {};
          res.data.sizeStock.forEach(s => stock[s.size] = s.quantity);
          setSizeStockMap(stock);
          setSelectedSize(res.data.sizeStock[0].size);
        }

        const rel = await axios.get(`https://wethrift-backend.onrender.com/api/product?category=${res.data.category}`);
        setRelatedProducts(rel.data.filter(p => p._id !== id).slice(0, 4));
      } catch (err) {
        console.error("DATA_FETCH_ERROR", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    if (selectedSize) setCurrentStockDisplay(sizeStockMap[selectedSize] || 0);
  }, [selectedSize, sizeStockMap]);

  const triggerAlert = (msg, type = "success") => {
    setAlertMsg(msg); setAlertType(type);
    setTimeout(() => setAlertMsg(""), 4000);
  };

  const calculateAISize = () => {
    if (!aiData.height || !aiData.weight) return triggerAlert("DATA_INCOMPLETE", "error");
    const rec = parseInt(aiData.weight) > 75 ? "XL" : "M";
    setSelectedSize(rec);
    setShowSizeAI(false);
    triggerAlert(`AI_OPTIMIZATION_COMPLETE: SIZE_${rec}`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if(!newReview.comment) return;
    try {
      const res = await axios.post(`https://wethrift-backend.onrender.com/api/product/${id}/review`, newReview);
      setReviews(res.data.reviews);
      setNewReview({ rating: 5, comment: "", username: "" });
      triggerAlert("FEEDBACK_LOGGED_SUCCESSFULLY");
    } catch (err) { triggerAlert("TRANSMISSION_FAILED", "error"); }
  };

  if (loading || !product) return (
    <div className="h-screen bg-white dark:bg-[#050505] flex flex-col items-center justify-center">
      <div className="relative">
        <div className="w-20 h-20 border-2 border-emerald-500/20 rounded-full"></div>
        <div className="absolute top-0 w-20 h-20 border-t-2 border-emerald-500 rounded-full animate-spin"></div>
      </div>
      <p className="mt-6 font-black text-emerald-500 uppercase tracking-[0.6em] animate-pulse text-[10px]">Initializing_Assets</p>
    </div>
  );

  const allImages = [product.img, ...(product.gallery || [])].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-[#080808] text-zinc-900 dark:text-zinc-100 transition-colors duration-500 selection:bg-emerald-500 selection:text-black">
      <SEO title={`${product.name} | Premium Collection`} />
      {alertMsg && <CustomAlert message={alertMsg} onClose={() => setAlertMsg("")} type={alertType} />}

      <div className="max-w-[1440px] mx-auto px-4 md:px-10 pt-24 pb-32">
        <div className="flex flex-col lg:flex-row gap-16 xl:gap-28">
          
          {/* --- GALLERY UNIT (STICKY ON DESKTOP) --- */}
          <div className="w-full lg:w-[52%]">
            <div className="lg:sticky lg:top-28 space-y-6">
              {/* Main Stage */}
              <div className="relative aspect-[4/5] rounded-[2.5rem] md:rounded-[4rem] overflow-hidden bg-white dark:bg-zinc-900 shadow-2xl group border border-zinc-200/50 dark:border-white/5">
                <div className="absolute top-6 left-6 z-10 flex flex-col gap-3">
                   <div className="bg-black/80 backdrop-blur-md text-white p-3 rounded-2xl hover:bg-emerald-500 transition-colors cursor-pointer"><Share2 size={18}/></div>
                </div>

                {allImages.map((img, i) => (
                  <img 
                    key={i} src={img} 
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-out ${activeImageIndex === i ? "opacity-100 scale-100" : "opacity-0 scale-105"}`} 
                    alt="Asset View" 
                  />
                ))}

                {/* Navigation HUD */}
                <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
                  <button onClick={() => setActiveImageIndex(prev => (prev === 0 ? allImages.length - 1 : prev - 1))} className="p-4 bg-white/10 backdrop-blur-xl text-white rounded-full pointer-events-auto opacity-0 group-hover:opacity-100 transition-all hover:bg-emerald-500 hover:text-black">
                    <ChevronLeft size={24}/>
                  </button>
                  <button onClick={() => setActiveImageIndex(prev => (prev === allImages.length - 1 ? 0 : prev + 1))} className="p-4 bg-white/10 backdrop-blur-xl text-white rounded-full pointer-events-auto opacity-0 group-hover:opacity-100 transition-all hover:bg-emerald-500 hover:text-black">
                    <ChevronRight size={24}/>
                  </button>
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className="flex gap-4 overflow-x-auto py-2 no-scrollbar justify-start md:justify-center">
                {allImages.map((img, i) => (
                  <button 
                    key={i} onClick={() => setActiveImageIndex(i)} 
                    className={`relative min-w-[90px] h-24 rounded-3xl overflow-hidden border-2 transition-all duration-500 flex-shrink-0 ${activeImageIndex === i ? "border-emerald-500 scale-105 shadow-xl shadow-emerald-500/20" : "border-transparent opacity-40 grayscale hover:grayscale-0 hover:opacity-100"}`}
                  >
                    <img src={img} className="w-full h-full object-cover" alt="Thumb" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* --- INFO ENGINE --- */}
          <div className="w-full lg:w-[48%] space-y-12">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-emerald-500 font-black tracking-[0.4em] uppercase text-[10px] bg-emerald-500/10 px-5 py-2 rounded-full border border-emerald-500/20">{product.brand}</span>
                <div className="flex items-center gap-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-900/50 rounded-full border border-zinc-200 dark:border-white/5">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                  <span className="text-[10px] font-black opacity-60 uppercase tracking-tighter">{viewers} PROTOCOLS_ACTIVE</span>
                </div>
              </div>
              
              <h1 className="text-5xl md:text-8xl font-black uppercase italic tracking-tighter leading-[0.8]">{product.name}</h1>
              <p className="text-zinc-500 dark:text-zinc-400 text-sm md:text-base font-medium leading-relaxed max-w-xl border-l-4 border-emerald-500/20 pl-6">{product.description}</p>
            </div>

            {/* Price Visualization */}
            <div className="relative p-10 bg-white dark:bg-zinc-900/40 rounded-[3.5rem] border border-zinc-200 dark:border-white/5 shadow-2xl overflow-hidden group">
              <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/5 blur-[80px]"></div>
              <div className="flex flex-wrap items-baseline gap-6">
                <span className="text-6xl md:text-8xl font-black italic text-emerald-500 tracking-tighter">₹{product.price}</span>
                <div className="flex flex-col">
                  <span className="text-2xl text-zinc-300 dark:text-zinc-700 line-through font-bold tracking-tighter">₹{product.mrp}</span>
                  <span className="text-[11px] font-black text-white bg-red-500 px-3 py-1 rounded-lg w-fit uppercase italic transform -rotate-2">SAVE_₹{product.mrp - product.price}</span>
                </div>
              </div>
            </div>

            {/* Neural Fit Modal Trigger */}
            <button onClick={() => setShowSizeAI(true)} className="group w-full p-[2px] bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500 rounded-[2.5rem] transition-all active:scale-[0.98]">
              <div className="bg-white dark:bg-zinc-950 p-6 rounded-[2.4rem] flex items-center justify-between">
                <div className="flex items-center gap-5 text-left">
                  <div className="p-4 bg-emerald-500 text-black rounded-2xl shadow-lg shadow-emerald-500/30 group-hover:rotate-[360deg] transition-transform duration-700"><Cpu size={28}/></div>
                  <div>
                    <p className="text-[12px] font-black uppercase tracking-widest text-emerald-500 italic">Neural_Size_Analysis</p>
                    <p className="text-[10px] text-zinc-400 uppercase font-bold tracking-tighter">Biometric data matching active</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                    {[1,2,3].map(i => <div key={i} className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: `${i*0.2}s`}}></div>)}
                </div>
              </div>
            </button>

            {/* Configuration selection */}
            <div className="space-y-6">
              <div className="flex justify-between items-end">
                <div className="space-y-1">
                  <p className="text-[11px] font-black uppercase text-zinc-400 tracking-[0.3em]">Hardware_Config</p>
                  <div className="flex gap-2">
                    <div className="h-1 w-12 bg-emerald-500 rounded-full"></div>
                    <div className="h-1 w-4 bg-zinc-200 dark:bg-zinc-800 rounded-full"></div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-red-500/10 px-4 py-2 rounded-2xl border border-red-500/20">
                  <BatteryCharging size={16} className="text-red-500 animate-pulse"/>
                  <span className="text-[10px] font-black text-red-500 uppercase tracking-widest leading-none">{currentStockDisplay} UNITS_REMAINING</span>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {Object.keys(sizeStockMap).map(size => (
                  <button 
                    key={size} onClick={() => setSelectedSize(size)} 
                    className={`group relative py-7 rounded-[2rem] border-2 font-black transition-all duration-500 overflow-hidden ${selectedSize === size ? "border-emerald-500 bg-emerald-500 text-black shadow-2xl shadow-emerald-500/40 -translate-y-2" : "border-zinc-200 dark:border-white/5 bg-white dark:bg-zinc-900/40 text-zinc-400 hover:border-emerald-500/50 hover:text-emerald-500"}`}
                  >
                    <span className="text-base uppercase italic relative z-10">{size}</span>
                    {selectedSize === size && <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent"></div>}
                  </button>
                ))}
              </div>
            </div>

            {/* Terminal Actions */}
            <div className="flex flex-col sm:flex-row gap-6 pt-6">
              <button onClick={() => toggleWishlist(product)} className={`p-9 rounded-[2.8rem] border-2 transition-all duration-500 flex items-center justify-center ${wishlistItems?.some(i => i._id === product._id) ? "bg-red-500 border-red-500 text-white shadow-xl shadow-red-500/30" : "border-zinc-200 dark:border-white/5 hover:border-red-500 hover:text-red-500"}`}>
                <Heart size={32} fill={wishlistItems?.some(i => i._id === product._id) ? "currentColor" : "none"} />
              </button>
              <button onClick={() => addToCart(product, selectedSize)} className="flex-1 bg-emerald-500 text-black font-black py-9 rounded-[2.8rem] flex items-center justify-center gap-5 text-sm tracking-[0.4em] italic hover:scale-[1.02] hover:shadow-[0_20px_60px_rgba(16,185,129,0.4)] transition-all active:scale-95 group">
                <ShoppingBag size={24}/> INITIALIZE_PURCHASE <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform"/>
              </button>
            </div>

            {/* Reliability Grid */}
            <div className="grid grid-cols-3 gap-6 pt-12 border-t border-zinc-200 dark:border-white/5">
              {[ 
                {i:<ShieldCheck size={24}/>, l:'ENCRYPTED_VAULT', d:'Secure Payment'}, 
                {i:<Truck size={24}/>, l:'RAPID_DISPATCH', d:'Fast Delivery'}, 
                {i:<Award size={24}/>, l:'AUTHENTIC_SYNC', d:'100% Genuine'} 
              ].map((b,idx)=>(
                <div key={idx} className="flex flex-col items-center text-center gap-3 group">
                  <div className="text-emerald-500 bg-emerald-500/10 p-4 rounded-2xl group-hover:bg-emerald-500 group-hover:text-black transition-colors">{b.i}</div>
                  <div>
                    <p className="text-[9px] font-black tracking-widest uppercase mb-1">{b.l}</p>
                    <p className="text-[8px] font-bold text-zinc-400 uppercase opacity-0 group-hover:opacity-100 transition-opacity">{b.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* --- CROSS-REFERENCE (RELATED) --- */}
        <div className="mt-56 space-y-16">
          <div className="flex items-center gap-8">
            <h2 className="text-4xl md:text-7xl font-black italic uppercase tracking-tighter">Parallel_Units</h2>
            <div className="flex-1 h-[2px] bg-gradient-to-r from-emerald-500/40 via-zinc-200 dark:via-zinc-800 to-transparent"></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-14">
            {relatedProducts.map(p => (
              <Link to={`/product/${p._id}`} key={p._id} className="group relative">
                <div className="aspect-[3/4] rounded-[3rem] overflow-hidden bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/5 mb-8 relative shadow-xl">
                  <img src={p.img} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 group-hover:rotate-2" alt={p.name} />
                  <div className="absolute top-4 right-4 bg-emerald-500 text-black p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all translate-y-4 group-hover:translate-y-0 shadow-lg"><ArrowRight size={18}/></div>
                </div>
                <div className="px-2">
                   <h3 className="font-black italic uppercase text-xs md:text-sm tracking-widest truncate group-hover:text-emerald-500 transition-colors mb-2">{p.name}</h3>
                   <div className="flex justify-between items-center">
                      <p className="text-emerald-500 font-black text-xl tracking-tighter">₹{p.price}</p>
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-tighter border-b border-zinc-400 pb-0.5">Details</span>
                   </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* --- INTELLIGENCE & FEEDBACK --- */}
        <div className="mt-56 grid lg:grid-cols-2 gap-32 relative">
          {/* User Logs */}
          <div className="space-y-20">
            <div className="space-y-6">
              <h2 className="text-6xl font-black italic uppercase tracking-tighter leading-none">Intelligence_Logs</h2>
              <div className="flex items-center gap-4">
                <div className="h-[3px] w-16 bg-emerald-500"></div>
                <p className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.5em] animate-pulse">Live_Archive_Synced</p>
              </div>
            </div>

            <div className="space-y-10 max-h-[900px] overflow-y-auto pr-6 custom-scrollbar">
              {reviews.length > 0 ? (
                reviews.map((rev, i) => (
                  <div key={i} className="group p-10 bg-white dark:bg-zinc-900/20 rounded-[3.5rem] border border-zinc-200 dark:border-white/5 hover:border-emerald-500/40 transition-all duration-700 shadow-xl hover:shadow-emerald-500/5">
                    <div className="flex justify-between items-start mb-8">
                      <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-3xl bg-zinc-100 dark:bg-black border border-zinc-200 dark:border-white/10 flex items-center justify-center text-emerald-500 shadow-inner group-hover:scale-110 transition-transform"><User size={28}/></div>
                        <div>
                          <p className="text-sm font-black uppercase italic tracking-widest mb-1">{rev.username}</p>
                          <div className="flex text-emerald-500 gap-1.5">
                            {[...Array(5)].map((_, s) => <Star key={s} size={12} fill={s < rev.rating ? "#10b981" : "none"} className={s < rev.rating ? "text-emerald-500" : "text-zinc-300 dark:text-zinc-800"} />)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                          <span className="text-[10px] font-black text-zinc-400 uppercase italic">ID: 00{i+1}</span>
                          <span className="flex items-center gap-1.5 bg-emerald-500/10 text-emerald-500 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter italic border border-emerald-500/20"><BadgeCheck size={12}/> Verified_Access</span>
                      </div>
                    </div>
                    <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed font-medium italic pl-8 border-l-2 border-emerald-500/30">"{rev.comment}"</p>
                    
                    {rev.adminReply && (
                      <div className="mt-10 relative pl-10 py-4 bg-emerald-500/[0.03] rounded-3xl border border-emerald-500/10">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-[2px] h-[70%] bg-emerald-500"></div>
                        <div className="flex items-center gap-2 mb-3"><div className="p-1 bg-emerald-500 rounded text-black"><BadgeCheck size={12}/></div><span className="text-[10px] font-black uppercase text-emerald-500 tracking-widest italic">Management_Response</span></div>
                        <p className="text-xs text-zinc-400 italic font-medium leading-relaxed">{rev.adminReply}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-24 text-center border-2 border-dashed border-zinc-200 dark:border-white/10 rounded-[4rem]">
                  <MessageSquare className="mx-auto text-zinc-300 dark:text-zinc-800 mb-6" size={60} />
                  <p className="text-zinc-400 font-black uppercase text-xs tracking-[0.4em]">Empty_Feed_Detected</p>
                </div>
              )}
            </div>
          </div>

          {/* Report Input Unit */}
          <div className="lg:sticky lg:top-28 h-fit">
            <div className="p-12 md:p-16 bg-white dark:bg-[#0A0A0A] rounded-[4rem] border border-zinc-200 dark:border-white/5 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-10"><MessageSquare size={120}/></div>
              <div className="space-y-3 mb-12 relative z-10">
                <h3 className="text-4xl font-black italic uppercase tracking-tighter">Initialize_Log</h3>
                <p className="text-[11px] text-zinc-400 font-bold uppercase tracking-[0.3em]">Sector_Feedback_Sync_Ready</p>
              </div>

              <form onSubmit={submitReview} className="space-y-10 relative z-10">
                <div className="space-y-5">
                  <div className="flex justify-between items-center px-2">
                    <p className="text-[10px] font-black uppercase text-emerald-500 tracking-widest italic">Core_Rating</p>
                    <span className="text-[10px] font-black text-zinc-400">VAL: {newReview.rating}.0</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    {[1, 2, 3, 4, 5].map(num => (
                      <button key={num} type="button" onClick={() => setNewReview({ ...newReview, rating: num })} className={`flex-1 py-6 rounded-3xl border-2 font-black transition-all duration-500 ${newReview.rating >= num ? "border-emerald-500 bg-emerald-500/10 text-emerald-500 shadow-[0_0_30px_rgba(16,185,129,0.1)]" : "border-zinc-100 dark:border-white/5 bg-zinc-50 dark:bg-black text-zinc-400"}`}>
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-8">
                    <div className="relative group">
                        <User className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-emerald-500 transition-colors" size={18}/>
                        <input required value={newReview.username} onChange={e => setNewReview({ ...newReview, username: e.target.value })} placeholder="IDENTITY_CREDENTIALS" className="w-full bg-zinc-50 dark:bg-black border border-zinc-200 dark:border-white/5 rounded-[1.8rem] py-6 pl-16 pr-8 text-xs font-black uppercase tracking-widest outline-none focus:border-emerald-500 transition-all shadow-inner" />
                    </div>
                    
                    <div className="relative group">
                        <Info className="absolute left-6 top-6 text-zinc-400 group-focus-within:text-emerald-500 transition-colors" size={18}/>
                        <textarea required value={newReview.comment} onChange={e => setNewReview({ ...newReview, comment: e.target.value })} placeholder="TRANSMIT_DETAILED_EXPERIENCE..." className="w-full bg-zinc-50 dark:bg-black border border-zinc-200 dark:border-white/5 rounded-[2.5rem] pt-6 pl-16 pr-8 pb-8 text-sm italic outline-none focus:border-emerald-500 h-56 transition-all resize-none shadow-inner" />
                    </div>
                </div>
                
                <button type="submit" className="w-full py-8 bg-emerald-500 text-black font-black uppercase text-xs tracking-[0.5em] rounded-[2rem] hover:shadow-[0_20px_50px_rgba(16,185,129,0.5)] transition-all active:scale-95 flex items-center justify-center gap-4">
                  UPLOAD_TO_ARCHIVE <Send size={18}/>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* --- NEURAL INTERFACE (MODALS) --- */}
      {showSizeAI && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-3xl animate-in fade-in duration-500"></div>
          <div className="bg-white dark:bg-zinc-950 w-full max-w-lg rounded-[4rem] p-12 border border-emerald-500/30 shadow-[0_0_100px_rgba(16,185,129,0.2)] relative animate-in zoom-in-95 duration-500">
            <div className="flex justify-between items-center mb-12">
              <div className="flex items-center gap-4 text-emerald-500">
                <div className="animate-pulse"><Cpu size={32}/></div>
                <div>
                  <h4 className="font-black italic text-2xl uppercase tracking-tighter leading-none">Neural_Fit_v3.4</h4>
                  <p className="text-[10px] font-bold opacity-60 uppercase tracking-widest mt-1">Biometric Scanner Active</p>
                </div>
              </div>
              <button onClick={() => setShowSizeAI(false)} className="p-4 bg-zinc-100 dark:bg-white/5 rounded-full hover:bg-red-500 hover:text-white transition-all"><X size={24}/></button>
            </div>
            
            <div className="space-y-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                    <p className="text-[10px] font-black uppercase text-zinc-400 ml-4 italic tracking-widest">Stature (CM)</p>
                    <input type="number" placeholder="180" className="w-full bg-zinc-50 dark:bg-black border border-zinc-200 dark:border-white/10 p-7 rounded-3xl outline-none focus:border-emerald-500 font-black text-xl text-center" onChange={e => setAIData({ ...aiData, height: e.target.value })} />
                </div>
                <div className="space-y-3">
                    <p className="text-[10px] font-black uppercase text-zinc-400 ml-4 italic tracking-widest">Mass (KG)</p>
                    <input type="number" placeholder="75" className="w-full bg-zinc-50 dark:bg-black border border-zinc-200 dark:border-white/10 p-7 rounded-3xl outline-none focus:border-emerald-500 font-black text-xl text-center" onChange={e => setAIData({ ...aiData, weight: e.target.value })} />
                </div>
              </div>
              <button onClick={calculateAISize} className="w-full py-8 bg-emerald-500 text-black font-black uppercase tracking-[0.3em] rounded-3xl hover:shadow-lg transition-all active:scale-95 shadow-xl shadow-emerald-500/20">Compute_Optimal_Fit</button>
            </div>
          </div>
        </div>
      )}

      {/* --- COLLABORATIVE FLOATING HUB --- */}
      <button onClick={() => setIsChatOpen(!isChatOpen)} className="fixed bottom-10 right-10 z-[4000] bg-emerald-500 text-black p-6 rounded-full shadow-[0_20px_50px_rgba(16,185,129,0.4)] hover:scale-110 active:scale-90 transition-all border-4 border-white dark:border-zinc-950 group">
        <Users size={32} className="group-hover:rotate-12 transition-transform"/>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 border-2 border-white rounded-full animate-bounce"></div>
      </button>

      {isChatOpen && (
        <div className="fixed bottom-32 right-10 z-[4000] w-[90%] md:w-[420px] animate-in slide-in-from-bottom-10 duration-500">
          <div className="bg-white dark:bg-zinc-950 border border-emerald-500/20 rounded-[3.5rem] overflow-hidden shadow-[0_30px_100px_rgba(0,0,0,0.5)]">
            <div className="p-8 bg-emerald-500 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-black text-emerald-500 rounded-2xl flex items-center justify-center"><Radio size={24} className="animate-pulse"/></div>
                <div>
                    <h5 className="font-black text-black uppercase tracking-tighter leading-none italic">Product_Hive</h5>
                    <p className="text-[10px] font-bold text-black/60 uppercase mt-1">Direct_Sync_Line</p>
                </div>
              </div>
              <button onClick={() => setIsChatOpen(false)} className="p-2 bg-black/10 rounded-full text-black hover:bg-black/20"><X size={20}/></button>
            </div>
            
            <div className="h-[400px] overflow-y-auto p-8 space-y-6 bg-zinc-50 dark:bg-[#050505]">
              {chatHistory.map((msg, i) => (
                <div key={i} className={`flex flex-col ${msg.username === user?.username ? 'items-end' : 'items-start'}`}>
                  <span className="text-[9px] font-black text-zinc-400 mb-2 uppercase italic px-2">{msg.username}</span>
                  <div className={`px-6 py-4 rounded-[1.8rem] text-[12px] font-bold max-w-[85%] shadow-sm ${msg.username === user?.username ? 'bg-emerald-500 text-black rounded-tr-none' : 'bg-white dark:bg-zinc-900 dark:text-zinc-100 border border-zinc-100 dark:border-white/5 rounded-tl-none'}`}>
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>
            
            <form onSubmit={e => { e.preventDefault(); if (!chatMessage) return; setChatHistory([...chatHistory, { username: user?.username || "GUEST_U", text: chatMessage }]); setChatMessage(""); }} className="p-6 bg-white dark:bg-zinc-900 border-t border-zinc-100 dark:border-white/5 flex gap-3">
              <input value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="Type comms..." className="flex-1 bg-zinc-100 dark:bg-black border border-transparent rounded-2xl px-6 py-4 text-xs font-bold outline-none focus:border-emerald-500 transition-all" />
              <button type="submit" className="p-4 bg-emerald-500 text-black rounded-2xl hover:scale-105 transition-transform shadow-lg shadow-emerald-500/20"><Send size={20}/></button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;